<template>
    <v-form ref="form" v-model="valid" data-cy="guest-category-form">
        <v-row class="mb-4 flex-wrap" dense>
            <v-col class="align-center">
                <v-switch
                    v-model="formData.active"
                    class="ml-auto width-max-content"
                    :label="formData.active ? $t('active') : $t('inactive')"
                    data-cy="status"
                    hide-details
                    density="compact"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <v-file-input
                    v-model="filesArray"
                    :label="$t('seat_map')"
                    accept="application/json"
                    density="compact"
                    @update:model-value="handleFileUpload"
                />
                <div
                    v-if="formData.schema && !isValidJSON"
                    class="text-subtitle-2 pt-1 text-red-darken-4 font-weight-regular ml-10"
                >
                    {{ $t('invalid_JSON') }}
                </div>
                <div class="ml-10 pb-10 d-flex flex-wrap gr-2 gc-5">
                    <span
                        v-if="formData.schema && isValidJSON"
                        class="text-link"
                        data-cy="view-seat-map"
                        @click="isShowModal = true"
                    >
                        {{ $t('view_seat_map') }}
                    </span>
                    <span class="text-link" data-cy="download-sample" @click="downloadSample">
                        {{ $t('download_sample') }}
                    </span>
                </div>
            </v-col>
        </v-row>
        <ModalWrapper v-model="isShowModal" :max-width="useWidthForSeatMap(maxCapacity)">
            <template v-slot:title>
                {{ $t('seat_map') }}
            </template>
            <template v-slot:content>
                <div v-if="formData.schema">
                    <SeatMap :seat-map="JSON.parse(formData.schema)" />
                </div>
            </template>
        </ModalWrapper>
        <v-row no-gutters class="gc-4" :class="{'flex-row-reverse': modalView}">
            <v-btn data-cy="save" :disabled="isLoading || !valid || !formData.schema" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" :disabled="isLoading" variant="text" @click="emit('cancel')">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import SeatMap from '~/components/SeatMap.vue'
    import {useWidthForSeatMap} from '~/composables/helpers'
    import {downloadData} from '@/utils/helpers'

    const props = defineProps<{
        linkedObjectId: string | number
        seatType: string
        maxCapacity?: string | number | null
        modalView?: boolean
    }>()

    const valid = ref<boolean>(false)
    const isLoading = ref<boolean>(false)
    const isShowModal = ref<boolean>(false)
    const isValidJSON = ref<boolean>(true)
    const formData = ref({active: true, schema: '', name: ''})
    const filesArray = ref(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    const emit = defineEmits<{
        (e: 'cancel'): void
    }>()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {linkedObjectId: props.linkedObjectId}
            const {
                seatMaps: [_seatMaps],
            } = await app.$api.seatMap.get(rq)

            if (_seatMaps) {
                formData.value = _seatMaps
            }
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            if (formData.value?.id) {
                await app.$api.seatMap.put(formData.value?.id, {
                    ...formData.value,
                })
            } else {
                await app.$api.seatMap.post({
                    linkedObjectId: props.linkedObjectId,
                    seatType: props.seatType,
                    ...formData.value,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const downloadSample = () => {
        const sample = {
            '1': ['DOOR', '4', '8', '12', '16', '20', null, null, '28', '32', '36', '40', '44', '49'],
            '2': [null, '3', '7', '11', '15', '19', null, null, '27', '31', '35', '39', '43', '48'],
            '3': [null, null, null, null, null, null, null, null, null, null, null, null, null, '47'],
            '4': [null, '2', '6', '10', '14', '18', '22', '24', '26', '30', '34', '38', '42', '46'],
            '5': ['DRIVER', '1', '5', '9', '13', '17', '21', '23', '25', '29', '33', '37', '41', '45'],
        }
        const jsonString = JSON.stringify(sample, null, 2)
        downloadData(jsonString, 'Bus.json')
    }

    const handleFileUpload = files => {
        isValidJSON.value = true
        if (files) {
            formData.value.name = files.name
            const reader = new FileReader()

            reader.onload = e => {
                try {
                    const json = JSON.parse(e.target.result as string)
                    formData.value.schema = JSON.stringify(json, null, 2)
                } catch (error) {
                    app.$toast.error(t('error_reading_file'))
                    isValidJSON.value = false
                }
            }

            reader.readAsText(files)
        } else {
            formData.value.name = ''
            formData.value.schema = ''
        }
    }
</script>
